import React, { useState } from 'react';
import { FaPlus, FaTimes, FaInfoCircle } from 'react-icons/fa';

/**
 * Step 4: Compatible Clients
 * - Multi-select or tag input
 * - Common clients with suggestions
 * - Custom client input
 */
const MCPServerFormStep4 = ({ formData, updateFormData }) => {
  const [customClient, setCustomClient] = useState('');

  const commonClients = [
    { name: 'Claude Desktop', logo: '💬' },
    { name: 'Cline', logo: '🔧' },
    { name: 'Continue', logo: '⚡' },
    { name: 'Zed', logo: '⚡' },
    { name: 'VS Code MCP', logo: '💻' },
    { name: 'Cursor', logo: '🖱️' },
    { name: 'Windsurf', logo: '🏄' },
    { name: 'Custom Client', logo: '🔌' },
  ];

  const toggleClient = (clientName) => {
    const currentClients = formData.compatibleClients || [];
    if (currentClients.includes(clientName)) {
      updateFormData({
        compatibleClients: currentClients.filter((c) => c !== clientName),
      });
    } else {
      updateFormData({
        compatibleClients: [...currentClients, clientName],
      });
    }
  };

  const addCustomClient = () => {
    if (customClient.trim() && !formData.compatibleClients.includes(customClient.trim())) {
      updateFormData({
        compatibleClients: [...formData.compatibleClients, customClient.trim()],
      });
      setCustomClient('');
    }
  };

  const removeClient = (clientName) => {
    updateFormData({
      compatibleClients: formData.compatibleClients.filter((c) => c !== clientName),
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomClient();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Compatible Clients
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Select the MCP clients that can connect to your server.
        </p>
      </div>

      {/* Common Clients Grid */}
      <div>
        <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
          Select Compatible Clients
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {commonClients.map((client) => {
            const isSelected = formData.compatibleClients.includes(client.name);
            return (
              <button
                key={client.name}
                type="button"
                onClick={() => toggleClient(client.name)}
                className={`
                  px-4 py-3 rounded-lg border-2 font-medium text-left transition-all
                  ${
                    isSelected
                      ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-spooky-bg-medium text-gray-700 dark:text-gray-300 hover:border-cyan-300'
                  }
                `}
              >
                <div className="text-2xl mb-1">{client.logo}</div>
                <div className="text-sm font-semibold">{client.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Clients Summary */}
      {formData.compatibleClients.length > 0 && (
        <div>
          <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
            Selected Clients ({formData.compatibleClients.length})
          </label>
          <div className="flex flex-wrap gap-2">
            {formData.compatibleClients.map((client) => (
              <span
                key={client}
                className="px-3 py-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-full text-sm font-semibold inline-flex items-center gap-2"
              >
                {client}
                <button
                  type="button"
                  onClick={() => removeClient(client)}
                  className="hover:text-cyan-900 dark:hover:text-cyan-300"
                >
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Custom Client Input */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Add Custom Client
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={customClient}
            onChange={(e) => setCustomClient(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter client name..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white"
          />
          <button
            type="button"
            onClick={addCustomClient}
            disabled={!customClient.trim()}
            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <FaPlus />
            Add
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          Add any other MCP clients not listed above
        </p>
      </div>

      {/* Info Box */}
      {formData.compatibleClients.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-400">
            <strong>Recommendation:</strong> Select at least one compatible client to help users
            understand where they can use your server.
          </p>
        </div>
      )}
    </div>
  );
};

export default MCPServerFormStep4;
